import { Box, Button, IconButton } from '@youversion/design-system-components';
import Dots3HorizontalStroked from '@youversion/design-system-icons/lib/Dots3HorizontalStroked';
import TriangleDownStroked from '@youversion/design-system-icons/lib/TriangleDownStroked';
import CurrencySelector from 'components/ui/dialogs/CurrencySelector';
import MyGivingDialog from 'components/ui/dialogs/MyGivingDialog';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { setFlutterNav } from 'src/utils/flutter/setFlutterNav';
import { useCurrency } from 'utils/store/currency';

interface Props {
  className?: string;
  showCurrencySelector?: boolean;
}

/**
 * Handles Mobile Header content.
 *
 * @param className - Classes for the header container.
 * @param showCurrencySelector - Determines if currency selector should be shown.
 *
 * @example
 *
 * ```typescriptreact
 * <MobileHeader
 *   onClick={handleClick}
 *   showCurrencySelector={true} />
 * ```
 */
export default function MobileHeader({ className, showCurrencySelector = true }: Props) {
  const { t } = useTranslation('common');
  const { currencies, selectedCurrency } = useCurrency();
  const [currencySelectorOpen, setCurrencySelectorOpen] = useState(false);
  const [myGivingDialogOpen, setMyGivingDialogOpen] = useState(false);
  const currencySelectorVisible = showCurrencySelector || (showCurrencySelector === undefined && currencies?.length);

  useEffect(() => {
    const anyDialogOpen = currencySelectorOpen || myGivingDialogOpen;
    setFlutterNav(!anyDialogOpen);
  }, [currencySelectorOpen, myGivingDialogOpen]);

  return (
    <>
      <header className={`${className} mbe-1 min-h-4 plb-3 p-1 flex justify-end`}>
        <Box className='flex flex-col items-center'>
          <Box className='flex items-center gap-2'>
            <AnimatePresence>
              {currencySelectorVisible ? (
                <Button
                  icon={TriangleDownStroked}
                  iconPosition='end'
                  id='btn_currency_selector'
                  onClick={() => {
                    setCurrencySelectorOpen(true);
                  }}
                  size='xs'
                  title={t('giving.titles.select_currency')}
                  variant='stroked'
                >
                  {selectedCurrency}
                </Button>
              ) : null}
            </AnimatePresence>
            <IconButton
              onClick={() => {
                setMyGivingDialogOpen(true);
              }}
            >
              <Dots3HorizontalStroked />
            </IconButton>
          </Box>
        </Box>
      </header>
      <AnimatePresence>
        {myGivingDialogOpen ? (
          <MyGivingDialog
            onClose={() => {
              setMyGivingDialogOpen(false);
            }}
          />
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {currencySelectorOpen ? (
          <CurrencySelector
            onClose={() => {
              setCurrencySelectorOpen(false);
            }}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
}
